import React, { useEffect, useState } from 'react';
import {
  DialogContent,
  DialogContentText,
  FormControl,
  FormGroup,
  FormHelperText,
  Input,
  InputAdornment,
  TextField,
} from '~/lib/bn-material-ui';
import { entityStore, useMeasurement, sirenActions, useSirenEntity } from '~/utility/BinSentry-ui-utility';
import { DateTime } from 'luxon';
import { BnDialog } from '~/common';

function initializeTime(now) {
  const quarterHour = parseInt(now.minute / 15) * 15;
  return now.set({ minute: quarterHour }); // rounded value
}

const DATE_FORMAT = 'yyyy-MM-dd\'T\'HH:mm';

export default function ManualBinLevel({ href, show, selectedLevelHref, handleClose }) {
  const [date, setDate] = useState(initializeTime(DateTime.now()));
  const [binLevel, setBinLevel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const editingExistingLevel = !!selectedLevelHref;

  const { entity } = useSirenEntity({ href });
  const { entity: selectedLevelEntity } = useSirenEntity({ href: selectedLevelHref });
  const { entity: binLevelsEntity } = useSirenEntity({ href: entity?.getSubEntityByRel('https://api.binsentry.com/rel/v2/bin-levels').href });
  const pref = useMeasurement({ name: 'orderAmount' });
  const deleteAction = selectedLevelEntity?.getAction('delete-bin-level');
  const updateAction = selectedLevelEntity?.getAction('update-bin-level');
  const createAction = binLevelsEntity?.getAction('create-bin-level');

  useEffect(() => {
    if (typeof selectedLevelEntity?.properties?.volume !== 'undefined') {
      const level = pref.convert({
        bulkDensity: entity?.properties?.bulkDensity,
        volume: selectedLevelEntity?.properties?.volume,
      });

      setBinLevel(level);
    }
    if (selectedLevelEntity?.properties?.publishedAt) {
      setDate(DateTime.fromISO(selectedLevelEntity?.properties?.publishedAt));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLevelHref, selectedLevelEntity?.getLinkByRel('self').href]);

  const invalidateAll = async () => {
    return Promise.all([
      entityStore.invalidateHref(entity.getSubEntityByRel('https://api.binsentry.com/rel/v2/bin-levels').href),
      entityStore.invalidateHref(entity.getSubEntityByRel('https://api.binsentry.com/rel/bin-stat-latest').href),
      entityStore.invalidateHref(entity.getSubEntityByRel('https://api.binsentry.com/rel/bin-level-latest').href),
      entityStore.invalidateHref(entity.getLinkByRel('self').href),
      entityStore.invalidateHref(binLevelsEntity.getLinkByRel('self').href),
    ]);
  };

  const saveBinLevel = async (volume) => {
    const action = editingExistingLevel ? updateAction : createAction;
    const fields = sirenActions.getActionFields(action);
    fields.set('volume', volume);
    !editingExistingLevel && fields.set('date', date.toUTC().toISO());

    setSubmitting(true);
    await sirenActions.submitAction(action, fields);
    await invalidateAll();
    setSubmitting(false);
  };

  const deleteSelectedBinLevel = async () => {
    setSubmitting(true);
    await sirenActions.submitAction(deleteAction);
    await invalidateAll();
    setSubmitting(false);
  };

  const dateIsValid = () => date < DateTime.now();

  const onDelete = async () => {
    await deleteSelectedBinLevel();
    handleClose();
  };

  const onSubmit = async () => {
    const volume = pref.reverse({
      bulkDensity: entity.properties.bulkDensity,
      ...(pref.metric === 'weight' ? { weight: binLevel } : { volume: binLevel }),
    });
    await saveBinLevel(volume);
    handleClose();
  };

  return (
    <BnDialog
      open={show}
      onClose={() => handleClose()}
      title={`${editingExistingLevel ? 'Edit' : 'Add'} Manual Inventory`}
      submitDisabled={(!createAction && !updateAction) || submitting || !dateIsValid() || !binLevel}
      onSubmit={onSubmit}
      submitLabel='Save'
      onDelete={deleteAction && onDelete}
    >
      <DialogContent>
        <DialogContentText>Regularly enter manual inventories collected at the farm to improve bin level accuracy of standard bins.</DialogContentText>
        <FormGroup>
          <FormControl margin='normal'>
            <Input
              disabled={!(updateAction || createAction)}
              value={binLevel}
              onChange={e => setBinLevel(e.target.value)}
              type='number'
              inputProps={{ min: '0' }}
              aria-describedby='bin-level-input'
              endAdornment={<InputAdornment position='end'>{pref.unit}</InputAdornment>}
            />
            <FormHelperText id='bin-level-input'>Bin Level</FormHelperText>
          </FormControl>
          <FormControl margin='normal'>
            <TextField
              disabled={editingExistingLevel}
              error={!dateIsValid()}
              type='datetime-local'
              value={date.toFormat(DATE_FORMAT)}
              helperText={dateIsValid() ? 'Inventory Taken At' : 'Date cannot be in the future'}
              onChange={e => { setDate(DateTime.fromFormat(e.target.value, DATE_FORMAT)); }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </FormControl>
        </FormGroup>
      </DialogContent>
    </BnDialog>
  );
}
